import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";

import SchoolIcon from "@mui/icons-material/School";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrainingOverviewTab from "./TrainingOverviewTab";
import DocxViewer from "../../../components/DocxViewer/DocxViewer";
import PdfViewer from "../../../components/PdfViewer/PdfViewer";
import VideoPlayer from "../../../components/VideoPlayer/VideoPlayer";
import PDFSlideViewer from "../../../components/PPTAsPDFViewer/PPTAsPDFViewer";
import { TrainingSectionTypeKey } from "../utils/enums";
import { useTheme } from "@mui/material/styles";

export default function TrainingDetailDialog({
  open,
  onClose,
  training,
  onStatusChange,
  refetch,
  trainingModuleStatusMap,
  trainingSectionMap,
  setSnackbarInfo,
}) {
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    if (!open) {
      setSelectedMaterial(null);
    }
  }, [open]);

  if (!training) return null;

  const handleBack = () => {
    setSelectedMaterial(null);
  };

  const renderViewer = () => {
    if (!selectedMaterial) return null;
    const { section_type_key } = selectedMaterial;
    const fileUrl = `${process.env.REACT_APP_POLICY_DOC_URL}/${selectedMaterial?.path}`;

    if (section_type_key === TrainingSectionTypeKey.PDF) {
      return <PdfViewer fileUrl={fileUrl} />;
    } else if (section_type_key === TrainingSectionTypeKey.DOCX) {
      return <DocxViewer fileUrl={fileUrl} />;
    } else if (section_type_key === TrainingSectionTypeKey.VIDEO) {
      return (
        <VideoPlayer
          fileUrl={fileUrl}
          showVideoSkipControls={true}
          resetOnWindowChange={false}
        />
      );
    } else if (section_type_key === TrainingSectionTypeKey.PPTX) {
      return <PDFSlideViewer fileUrl={fileUrl} />;
    } else {
      return (
        <Box
          sx={{
            height: "50vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography>Unsupported file type.</Typography>
        </Box>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          maxHeight: "90vh",
          minHeight: "50vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "16px",
          background: theme.customColors.background.cardHeader,
          borderBottom: "1px solid",
          borderColor: theme.customColors.border.main,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {selectedMaterial ? (
            <IconButton onClick={handleBack}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : (
            <SchoolIcon sx={{ fontSize: 20 }} />
          )}
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {selectedMaterial ? selectedMaterial.title : training?.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
              }}
            >
              {selectedMaterial
                ? selectedMaterial.description
                : training?.description ||
                "No description available for this training course."}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          // onClick={onClose}
          onClick={() => {
            if (selectedMaterial) {
              handleBack(); // Go back instead of closing
            } else {
              onClose(); // No material selected → close the dialog
            }
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.customColors.text.disabled,
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflowY: "auto" }}>
        {selectedMaterial ? (
          renderViewer()
        ) : (
          <TrainingOverviewTab
            training={training}
            onStatusChange={onStatusChange}
            onClose={onClose}
            refetch={refetch}
            onOpenMaterial={(material) => setSelectedMaterial(material)}
            trainingModuleStatusMap={trainingModuleStatusMap}
            trainingSectionMap={trainingSectionMap}
            setSnackbarInfo={setSnackbarInfo}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
